import { Filter as FilterIcon, Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '~/components/ui/button';
import { Checkbox } from '~/components/ui/checkbox';
import { Input } from '~/components/ui/input';
import { Label } from '~/components/ui/label';
import { Sheet, SheetContent, SheetTitle } from '~/components/ui/sheet';
import { Switch } from '~/components/ui/switch';
import { GRADE_OPTIONS, SOURCE_STATUS_OPTIONS } from '~/features/explorer/utils/products-catalog';

/** The subset of product filters edited inside the drawer (search + sort stay
 *  in the toolbar). */
export interface ProductFilterDraft {
  grade: string;
  sourceStatus: string;
  hasCriticalIssues: boolean;
  vendors: string[];
}

interface ProductFilterDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Committed filter values — seed the draft each time the drawer opens. */
  value: ProductFilterDraft;
  /** Vendor (brand) universe for the multi-select section. */
  vendorOptions: string[];
  /** Fires with the drafted values when the user applies. */
  onApply: (next: ProductFilterDraft) => void;
}

/** Auto-enable the option search box once a list grows past this size. */
const OPTION_SEARCH_THRESHOLD = 12;
const CB = 'h-5 w-5 rounded';

/** Number of meaningful selections in a draft — drives the Apply badge. */
function countActive(draft: ProductFilterDraft): number {
  return (
    (draft.grade ? 1 : 0) +
    (draft.sourceStatus ? 1 : 0) +
    (draft.hasCriticalIssues ? 1 : 0) +
    draft.vendors.length
  );
}

function SectionHeader({ label, count }: { label: string; count: number }) {
  const { t } = useTranslation('explorer');
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm font-semibold">{label}</span>
      {count > 0 && (
        <span className="text-primary text-xs font-semibold">
          {t('products.selected', { count })}
        </span>
      )}
    </div>
  );
}

/** Single-select checkbox list (radio-like): selecting an option replaces the
 *  value; clicking the active option clears it. */
function SingleSelectSection({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: readonly string[];
  value: string;
  onChange: (next: string) => void;
}) {
  return (
    <div className="space-y-2.5">
      <SectionHeader label={label} count={value ? 1 : 0} />
      <div className="bg-surface-stat-card space-y-1 rounded-xl p-3">
        {options.map((option) => {
          const id = `${label}-${option}`;
          return (
            <div key={option} className="flex items-center gap-3 py-1.5">
              <Checkbox
                id={id}
                className={CB}
                checked={value === option}
                onCheckedChange={() => {
                  onChange(value === option ? '' : option);
                }}
              />
              <Label htmlFor={id} className="cursor-pointer text-sm capitalize">
                {option}
              </Label>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** Multi-select checkbox list with an optional search box for long lists. */
function VendorSection({
  label,
  options,
  selected,
  onToggle,
}: {
  label: string;
  options: string[];
  selected: string[];
  onToggle: (value: string) => void;
}) {
  const { t } = useTranslation('explorer');
  const [query, setQuery] = useState('');
  const showSearch = options.length > OPTION_SEARCH_THRESHOLD;
  const filtered = useMemo(() => {
    if (!query.trim()) return options;
    const q = query.toLowerCase();
    return options.filter((o) => o.toLowerCase().includes(q));
  }, [options, query]);

  return (
    <div className="space-y-2.5">
      <SectionHeader label={label} count={selected.length} />
      <div className="bg-surface-stat-card space-y-2 rounded-xl p-3">
        {showSearch && (
          <div className="relative">
            <Search
              className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2"
              aria-hidden="true"
            />
            <Input
              placeholder={t('products.searchVendors')}
              aria-label={t('products.searchVendors')}
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
              }}
              className="bg-background h-9 rounded-xl pl-9 text-sm shadow-none"
            />
          </div>
        )}
        <div className="max-h-[280px] space-y-1 overflow-y-auto">
          {filtered.map((option) => {
            const id = `vendor-${option}`;
            return (
              <div key={option} className="flex items-center gap-3 py-1.5">
                <Checkbox
                  id={id}
                  className={CB}
                  checked={selected.includes(option)}
                  onCheckedChange={() => {
                    onToggle(option);
                  }}
                />
                <Label htmlFor={id} className="cursor-pointer truncate text-sm">
                  {option}
                </Label>
              </div>
            );
          })}
          {filtered.length === 0 && (
            <p className="text-muted-foreground py-2 text-xs">{t('products.noVendorsFound')}</p>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Right-side product filter drawer (app pattern): grade, source status,
 * critical-issues toggle, and vendor multi-select edited as a draft, committed
 * on Apply. Search and sort remain in the toolbar.
 */
export function ProductFilterDrawer({
  open,
  onOpenChange,
  value,
  vendorOptions,
  onApply,
}: ProductFilterDrawerProps) {
  const { t } = useTranslation('explorer');
  const [draft, setDraft] = useState(value);

  // Re-seed the draft on each open transition without an effect (derived-state
  // pattern) so reopening discards any uncommitted edits.
  const [lastOpen, setLastOpen] = useState(open);
  if (open !== lastOpen) {
    setLastOpen(open);
    if (open) setDraft(value);
  }

  const totalSelected = countActive(draft);
  const hasVendors = vendorOptions.length > 0;

  function toggleVendor(vendor: string) {
    setDraft((prev) => ({
      ...prev,
      vendors: prev.vendors.includes(vendor)
        ? prev.vendors.filter((v) => v !== vendor)
        : [...prev.vendors, vendor],
    }));
  }

  function handleReset() {
    setDraft({ grade: '', sourceStatus: '', hasCriticalIssues: false, vendors: [] });
  }

  function handleApply() {
    onApply(draft);
    onOpenChange(false);
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        style={{
          top: '1rem',
          bottom: '1rem',
          right: '1rem',
          height: 'auto',
          width: '380px',
          maxWidth: '380px',
        }}
        className="flex flex-col gap-0 rounded-2xl border-0 p-0 shadow-2xl"
      >
        <div className="flex items-center gap-2 border-b px-4 py-3 pr-10">
          <FilterIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
          <SheetTitle className="text-base font-semibold">{t('products.filters')}</SheetTitle>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto px-4 py-4">
          <SingleSelectSection
            label={t('products.grade')}
            options={GRADE_OPTIONS}
            value={draft.grade}
            onChange={(grade) => {
              setDraft((prev) => ({ ...prev, grade }));
            }}
          />
          <div className="border-border/60 border-t" />
          <SingleSelectSection
            label={t('products.sourceStatus')}
            options={SOURCE_STATUS_OPTIONS}
            value={draft.sourceStatus}
            onChange={(sourceStatus) => {
              setDraft((prev) => ({ ...prev, sourceStatus }));
            }}
          />
          <div className="border-border/60 border-t" />
          <div className="space-y-2.5">
            <SectionHeader
              label={t('products.criticalIssues')}
              count={draft.hasCriticalIssues ? 1 : 0}
            />
            <div className="bg-surface-stat-card flex items-center justify-between rounded-xl p-3">
              <Label htmlFor="critical-toggle" className="cursor-pointer text-sm font-medium">
                {t('products.criticalIssues')}
              </Label>
              <Switch
                id="critical-toggle"
                checked={draft.hasCriticalIssues}
                onCheckedChange={(hasCriticalIssues) => {
                  setDraft((prev) => ({ ...prev, hasCriticalIssues }));
                }}
                className="cursor-pointer"
              />
            </div>
          </div>
          {hasVendors && (
            <>
              <div className="border-border/60 border-t" />
              <VendorSection
                label={t('products.vendors')}
                options={vendorOptions}
                selected={draft.vendors}
                onToggle={toggleVendor}
              />
            </>
          )}
        </div>

        <div className="flex gap-3 border-t px-4 py-4">
          <Button
            variant="outline"
            onClick={handleReset}
            className="h-11 flex-1 cursor-pointer rounded-xl text-sm font-semibold"
          >
            {t('products.resetFilters')}
          </Button>
          <Button
            onClick={handleApply}
            className="h-11 flex-1 cursor-pointer gap-2 rounded-xl text-sm font-semibold"
          >
            {t('products.applyFilters')}
            {totalSelected > 0 && (
              <span className="text-primary inline-flex h-6 min-w-6 items-center justify-center rounded-lg bg-white px-1.5 text-xs font-bold tabular-nums">
                {totalSelected}
              </span>
            )}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
